import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptBusinessStaffInvite,
  activateBusinessManagementContext,
  applyForBusiness,
  archiveBusinessDepartment,
  createBusinessDepartment,
  createBusinessService,
  archiveBusinessService,
  getBusinessCatalog,
  getBusinessCategories,
  getBusinessBillingInvoices,
  getBusinessDirectory,
  getBusinessStaffInvite,
  getBusinessStaff,
  getManagedBusinessProfile,
  getMyBusinessMemberships,
  getOwnedBusinesses,
  getPublicBusinessProfile,
  inviteBusinessStaff,
  removeBusinessStaffLink,
  requestBusinessStaffLink,
  updateBusinessDepartment,
  updateBusinessProfile,
  updateBusinessService,
  updateBusinessStaffPermissions,
  uploadBusinessCover,
  uploadBusinessLogo,
} from "@/features/businesses/api/businessesApi";
import { refreshAuthStateForCurrentSession, invalidateAccountContextQueries } from "@/features/auth/hooks/useAuthMutations";
import { useAuthStore } from "@/lib/auth/authStore";
import type {
  DepartmentCreatePayload,
  DepartmentUpdatePayload,
  BusinessApplicationPayload,
  ProfileUpdatePayload,
  ServiceCreatePayload,
  ServiceUpdatePayload,
  StaffInvitePayload,
  StaffPermissionsPayload,
  StaffSelfRequestPayload,
} from "@/features/businesses/types";

export const businessKeys = {
  root: ["businesses"] as const,
  directory: (params?: { q?: string; city?: string; category?: string }) =>
    ["businesses", "directory", params?.q ?? "", params?.city ?? "", params?.category ?? ""] as const,
  publicProfile: (slug: string) => ["businesses", "public", slug] as const,
  managedProfile: (businessId: number | null) => ["businesses", "managed-profile", businessId] as const,
  owned: ["businesses", "owned"] as const,
  mine: ["businesses", "mine"] as const,
  staff: (businessId: number | null) => ["businesses", "staff", businessId] as const,
  catalog: (businessId: number | null) => ["businesses", "catalog", businessId] as const,
  staffInvite: (linkId: number | null) => ["businesses", "staff-invite", linkId] as const,
  categories: ["businesses", "categories"] as const,
  billing: (businessId: number | null) => ["businesses", "billing", businessId] as const,
};

export const useBusinessCategories = () =>
  useQuery({
    queryKey: businessKeys.categories,
    queryFn: getBusinessCategories,
    staleTime: 10 * 60_000,
  });

export const useBusinessDirectory = (params?: { q?: string; city?: string; category?: string }) =>
  useQuery({
    queryKey: businessKeys.directory(params),
    queryFn: () => getBusinessDirectory(params),
    staleTime: 60_000,
  });

export const usePublicBusinessProfile = (slug: string | undefined) =>
  useQuery({
    queryKey: businessKeys.publicProfile(slug ?? ""),
    queryFn: () => getPublicBusinessProfile(slug ?? ""),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });

export const useManagedBusinessProfile = (businessId: number | null) =>
  useQuery({
    queryKey: businessKeys.managedProfile(businessId),
    queryFn: () => getManagedBusinessProfile(businessId ?? 0),
    enabled: businessId !== null,
    staleTime: 60_000,
  });

export const useOwnedBusinesses = (enabled = true) =>
  useQuery({
    queryKey: businessKeys.owned,
    queryFn: getOwnedBusinesses,
    enabled,
    staleTime: 60_000,
  });

export const useApplyForBusiness = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BusinessApplicationPayload) => applyForBusiness(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.owned });
    },
  });
};

export const useBusinessStaff = (businessId: number | null) =>
  useQuery({
    queryKey: businessKeys.staff(businessId),
    queryFn: () => getBusinessStaff(businessId ?? 0),
    enabled: businessId !== null,
  });

export const useBusinessBillingInvoices = (businessId: number | null) =>
  useQuery({
    queryKey: businessKeys.billing(businessId),
    queryFn: () => getBusinessBillingInvoices(businessId ?? 0),
    enabled: businessId !== null,
  });

export const useBusinessCatalog = (businessId: number | null) =>
  useQuery({
    queryKey: businessKeys.catalog(businessId),
    queryFn: () => getBusinessCatalog(businessId ?? 0),
    enabled: businessId !== null,
  });

export const useMyBusinessMemberships = (enabled = true) =>
  useQuery({
    queryKey: businessKeys.mine,
    queryFn: getMyBusinessMemberships,
    enabled,
    staleTime: 60_000,
  });

export const useBusinessStaffInvite = (linkId: number | null) =>
  useQuery({
    queryKey: businessKeys.staffInvite(linkId),
    queryFn: () => getBusinessStaffInvite(linkId ?? 0),
    enabled: linkId !== null,
    staleTime: 30_000,
  });

export const useInviteBusinessStaff = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StaffInvitePayload) => inviteBusinessStaff(businessId ?? 0, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.staff(businessId) });
    },
  });
};

export const useRequestBusinessStaffLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ businessId, payload }: { businessId: number; payload: StaffSelfRequestPayload }) =>
      requestBusinessStaffLink(businessId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.root });
    },
  });
};

export const useActivateBusinessManagementContext = () =>
  useMutation({
    mutationFn: (businessId: number) => activateBusinessManagementContext(businessId),
    onSuccess: async (response) => {
      const store = useAuthStore.getState();
      if (response.access_token) {
        store.setAccessToken(response.access_token);
      }
      store.setAccountContext({ activeContext: response.active_context });
      await refreshAuthStateForCurrentSession();
      await invalidateAccountContextQueries();
    },
  });

export const useAcceptBusinessStaffInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => acceptBusinessStaffInvite(linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.owned });
      void queryClient.invalidateQueries({ queryKey: businessKeys.root });
    },
  });
};

export const useRemoveBusinessStaffLink = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => removeBusinessStaffLink(linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.staff(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
    },
  });
};

export const useUpdateBusinessStaffPermissions = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, payload }: { linkId: number; payload: StaffPermissionsPayload }) =>
      updateBusinessStaffPermissions(linkId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.staff(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
    },
  });
};

export const useCreateBusinessDepartment = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DepartmentCreatePayload) => createBusinessDepartment(businessId ?? 0, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
    },
  });
};

export const useCreateBusinessService = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ServiceCreatePayload) => createBusinessService(businessId ?? 0, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
    },
  });
};

export const useUpdateBusinessService = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, payload }: { serviceId: number; payload: ServiceUpdatePayload }) =>
      updateBusinessService(businessId ?? 0, serviceId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
    },
  });
};

export const useArchiveBusinessService = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: number) => archiveBusinessService(businessId ?? 0, serviceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
    },
  });
};

export const useUpdateBusinessDepartment = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, payload }: { departmentId: number; payload: DepartmentUpdatePayload }) =>
      updateBusinessDepartment(businessId ?? 0, departmentId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) }),
  });
};

export const useArchiveBusinessDepartment = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: number) => archiveBusinessDepartment(businessId ?? 0, departmentId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: businessKeys.catalog(businessId) }),
  });
};

export const useUpdateBusinessProfile = (businessId: number | null, slug: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileUpdatePayload) => updateBusinessProfile(businessId ?? 0, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.publicProfile(slug ?? "") });
      void queryClient.invalidateQueries({ queryKey: businessKeys.owned });
    },
  });
};

export const useUploadBusinessLogo = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadBusinessLogo(businessId ?? 0, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.owned });
    },
  });
};

export const useUploadBusinessCover = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadBusinessCover(businessId ?? 0, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.managedProfile(businessId) });
      void queryClient.invalidateQueries({ queryKey: businessKeys.owned });
    },
  });
};
